import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { InventoryLevel } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@nucleus-commerce/ui/components/sheet';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useInventoryMovements } from '../hooks/use-inventory-movements';

import { MovementList } from './movement-list';

export interface MovementsSheetProps {
  storeId: string;
  level: InventoryLevel | null;
  locationName: string | undefined;
  onClose: () => void;
}

/** The ledger of one variant at the location on screen, newest first. */
export function MovementsSheet({ storeId, level, locationName, onClose }: MovementsSheetProps) {
  const { t } = useTranslation('inventory');
  const query = useInventoryMovements(
    storeId,
    { variantId: level?.variantId, locationId: level?.locationId },
    level !== null,
  );
  const movements = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Sheet open={level !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        {level ? (
          <>
            <SheetHeader>
              <SheetTitle>{t('movements.title')}</SheetTitle>
              <SheetDescription>
                {t('movements.description', {
                  product: level.productTitle,
                  variant: level.variantTitle,
                  location: locationName ?? '',
                })}
              </SheetDescription>
            </SheetHeader>
            <div className="px-4 pb-6">
              {query.isPending ? (
                <Skeleton className="h-40" />
              ) : query.error ? (
                <ErrorState error={query.error} onRetry={() => void query.refetch()} />
              ) : movements.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t('movements.empty')}</p>
              ) : (
                <MovementList storeId={storeId} movements={movements} />
              )}
              {query.hasNextPage ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  {t('movements.more')}
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
